// SPDX-License-Identifier: AGPL-3.0-or-later
import {LIMIT_KEYS, type LimitKey} from '@app/vendor/constants/LimitConfigMetadata';
import type {ILimitConfigCodec, LimitConfigCodecOptions} from '@app/vendor/limits/ILimitConfigCodec';
import {DEFAULT_FREE_LIMITS} from '@app/vendor/limits/LimitDefaults';
import {computeDefaultsHash} from '@app/vendor/limits/LimitHashing';
import type {LimitConfigSnapshot, LimitConfigWireFormat} from '@app/vendor/limits/LimitTypes';
export class LimitConfigCodec implements ILimitConfigCodec {
	computeOverrides(
		fullLimits: Partial<Record<LimitKey, number>>,
		defaults: Record<LimitKey, number>,
	): Partial<Record<LimitKey, number>> {
		const overrides: Partial<Record<LimitKey, number>> = {};
		for (const key of LIMIT_KEYS) {
			const value = fullLimits[key];
			if (value === undefined) {
				continue;
			}
			if (value !== defaults[key]) {
				overrides[key] = value;
			}
		}
		return overrides;
	}
	toWireFormat(config: LimitConfigSnapshot, options?: LimitConfigCodecOptions): LimitConfigWireFormat {
		const defaults = options?.defaults ?? DEFAULT_FREE_LIMITS;
		const defaultsHash = options?.defaultsHash ?? computeDefaultsHash();
		const rules = config.rules.map((rule) => {
			const overrides = this.computeOverrides(rule.limits, defaults);
			return {
				id: rule.id,
				filters: rule.filters,
				overrides,
			};
		});
		return {
			version: 2,
			traitDefinitions: config.traitDefinitions,
			rules,
			defaultsHash,
		};
	}
	fromWireFormat(wireFormat: LimitConfigWireFormat, options?: LimitConfigCodecOptions): LimitConfigSnapshot {
		const defaults = options?.defaults ?? DEFAULT_FREE_LIMITS;
		const rules = wireFormat.rules.map((rule) => {
			const limits: Partial<Record<LimitKey, number>> = {
				...defaults,
				...rule.overrides,
			};
			return {
				id: rule.id,
				filters: rule.filters,
				limits,
			};
		});
		return {
			version: wireFormat.version,
			traitDefinitions: wireFormat.traitDefinitions,
			rules,
		};
	}
}
